import type { ComponentType, SVGProps } from "react"
import {
	IconAntennaFill18,
	IconBellFill18,
	IconCircleSignalFill18,
	IconInboxArrowDownFill18,
	IconNotificationFill18,
	IconRadarFill18,
} from "nucleo-ui-fill-18"
import {
	IconAntennaOutline18,
	IconBellOutline18,
	IconCircleDottedOutline18,
	IconCircleSignalOutline18,
	IconCircleUserOutline18,
	IconInboxArrowDownOutline18,
	IconNotificationOutline18,
	IconRadarOutline18,
} from "nucleo-ui-outline-18"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

type Icon = ComponentType<SVGProps<SVGSVGElement>>

const OPTIONS: { name: string; note: string; outline: Icon; fill: Icon }[] = [
	{
		name: "Today: Bell",
		note: "For comparison",
		outline: IconBellOutline18,
		fill: IconBellFill18,
	},
	{
		name: "1. Notification",
		note: "Square with a dot on the corner",
		outline: IconNotificationOutline18,
		fill: IconNotificationFill18,
	},
	{
		name: "2. Circle signal",
		note: "A point sending out rings",
		outline: IconCircleSignalOutline18,
		fill: IconCircleSignalFill18,
	},
	{
		name: "3. Radar",
		note: "Watching for what changed",
		outline: IconRadarOutline18,
		fill: IconRadarFill18,
	},
	{
		name: "4. Inbox arrow",
		note: "Something came in",
		outline: IconInboxArrowDownOutline18,
		fill: IconInboxArrowDownFill18,
	},
	{
		name: "5. Antenna",
		note: "Receiving a signal",
		outline: IconAntennaOutline18,
		fill: IconAntennaFill18,
	},
]

function Row({
	icon: RowIcon,
	label,
	active,
	count,
}: {
	icon: Icon
	label: string
	active?: boolean
	count?: number
}) {
	return (
		<div
			className={`flex h-8 items-center gap-2.5 rounded-md px-2 text-[14px] ${
				active
					? "bg-gray-150 font-medium text-primary dark:bg-gray-800"
					: "text-primary hover:bg-gray-100 dark:hover:bg-gray-800/60"
			}`}
		>
			<RowIcon className="h-[18px] w-[18px] flex-none transform-gpu text-gray-600 dark:text-gray-300" />
			<span className="flex-1 truncate">{label}</span>
			{count !== undefined && (
				<span className="text-xs text-secondary">{count}</span>
			)}
		</div>
	)
}

function Sidebar({
	option,
	active,
}: {
	option: (typeof OPTIONS)[number]
	active: boolean
}) {
	return (
		<div className="flex w-[240px] flex-col gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700/80 dark:bg-gray-900">
			<Row
				icon={active ? option.fill : option.outline}
				label="Notifications"
				active={active}
				count={3}
			/>
			<Row icon={IconCircleDottedOutline18} label="Unassigned" />
			<Row icon={IconCircleUserOutline18} label="My inbox" count={8} />
		</div>
	)
}

export function NotificationsIconPage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-white dark:bg-gray-950">
			<div className="mx-auto max-w-[1280px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Notifications icon, five options
					</div>
					<div className="text-sm text-secondary">
						Left: idle, outline. Right: selected, fill.
					</div>
				</div>
				<div className="flex flex-wrap gap-x-10 gap-y-8">
					{OPTIONS.map((o) => (
						<div key={o.name} className="flex flex-col gap-2">
							<div>
								<div className="text-sm font-medium text-primary">{o.name}</div>
								<div className="text-xs text-secondary">{o.note}</div>
							</div>
							<div className="flex gap-3">
								<Sidebar option={o} active={false} />
								<Sidebar option={o} active />
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
